import React, { useState, useMemo } from "react";

const WebinarTable = ({ bookings, webinarNames }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const webinarStats = useMemo(() => {
    const map = {};
    bookings?.forEach(b => {
      const id = b?.webinar_id || "Unbekannt";
      if (!map?.[id]) {
        map[id] = { id, name: webinarNames?.[id] || id, total: 0, onsite: 0, phone: 0, lastDate: null, bookings: [] };
      }
      map[id].total++;
      if (b?.type === "onsite") map[id].onsite++;
      else map[id].phone++;
      if (!map?.[id]?.lastDate || b?.date > map?.[id]?.lastDate) map[id].lastDate = b?.date;
      map?.[id]?.bookings?.push(b);
    });
    return Object.values(map)?.sort((a, b) => b?.total - a?.total);
  }, [bookings, webinarNames]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "–";
    try {
      const p = dateStr?.split('-'); if (p?.length === 3) return `${p[2]}.${p[1]}.${p[0]}`; return dateStr;
    } catch { return dateStr; }
  };

  return (
    <div style={{ background: 'white', borderRadius: '20px', border: '1px solid #dde1e7', boxShadow: '0 4px 6px rgba(62,73,84,0.04), 0 10px 20px rgba(62,73,84,0.06)', marginBottom: '24px', overflow: 'hidden' }}>
      {/* Card Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #dde1e7', display: 'flex', alignItems: 'center', gap: '10px', background: '#3E4954' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.82v6.36a1 1 0 0 1-1.447.894L15 14M3 8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z"/>
          </svg>
        </div>
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '16px', fontWeight: 700, color: 'white', margin: 0 }}>Webinar Performance</h2>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', fontFamily: 'var(--font-ui)', margin: 0 }}>Klicken Sie auf eine Zeile für Details</p>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-ui)' }}>
          <thead>
            <tr style={{ background: '#f5f6f8' }}>
              {['Webinar-ID', 'Webinar-Name', 'Buchungen', 'Vor-Ort', 'Telefon', 'Letzter Termin', '']?.map(col => (
                <th key={col} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#6b7a8d', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {webinarStats?.map((ws) => (
              <React.Fragment key={ws?.id}>
                <tr
                  className="dashboard-table-row"
                  style={{ cursor: 'pointer', borderBottom: '1px solid #dde1e7', transition: 'background 150ms ease' }}
                  onClick={() => setExpandedRow(expandedRow === ws?.id ? null : ws?.id)}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ display: 'inline-flex', padding: '3px 10px', background: 'rgba(62, 73, 84, 0.08)', color: '#3E4954', borderRadius: '100px', fontSize: '12px', fontWeight: 700 }}>
                      {ws?.id}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: 600, color: '#1e2b38' }}>{ws?.name}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 800, color: '#3E4954', fontFamily: 'var(--font-heading)' }}>{ws?.total}</span>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '14px', color: '#1e2b38' }}>{ws?.onsite}</td>
                  <td style={{ padding: '14px 16px', fontSize: '14px', color: '#1e2b38' }}>{ws?.phone}</td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#6b7a8d' }}>{formatDate(ws?.lastDate)}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <svg
                      width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7a8d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                      style={{ transform: expandedRow === ws?.id ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}
                    >
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </td>
                </tr>
                {expandedRow === ws?.id && (
                  <tr>
                    <td colSpan={7} style={{ padding: '0', background: '#f9fafb' }}>
                      <div style={{ padding: '16px 24px', borderBottom: '1px solid #dde1e7' }}>
                        <p style={{ fontSize: '12px', fontWeight: 700, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
                          Einzelbuchungen aus {ws?.name}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {ws?.bookings?.map(b => (
                            <div key={b?.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'white', borderRadius: '10px', border: '1px solid #dde1e7', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e2b38', minWidth: '140px' }}>{b?.vorname} {b?.nachname}</span>
                              <span style={{ fontSize: '12px', color: '#6b7a8d' }}>{b?.email}</span>
                              <span style={{ fontSize: '12px', color: '#6b7a8d', marginLeft: 'auto' }}>{formatDate(b?.date)} · {b?.time} Uhr</span>
                              <span style={{
                                fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '100px',
                                background: b?.type === 'onsite' ? 'rgba(5, 150, 105, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                color: b?.type === 'onsite' ? '#059669' : '#3b82f6',
                              }}>
                                {b?.type === 'onsite' ? 'Vor-Ort' : 'Telefon'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WebinarTable;
